<template>
  <div v-if="tournamentUi" class="tournament-info" data-qe-id="tournament-info">
    <div class="layout">
      <h3 v-if="headline" class="headline">{{ headline }}</h3>
      <div v-if="gameName" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.gameName') }}</div>
        <div class="value" data-qe-id="gametype">{{ $t('generic.game.name_' + tournamentUi.gametype) }}</div>
      </div>
      <div v-if="tournamentUi.gameVariant === 'speed'" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.variant') }}</div>
        <div class="value" data-qe-id="speedSkat">{{ $t('tournamentInfo.value.variant_speedSkat') }}</div>
      </div>
      <div v-if="totalWin" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.totalWin') }}</div>
        <div class="value" data-qe-id="prize-total">
          {{ $t('tournamentInfo.value.totalWin', { totalWin: tournamentUi.metaData.totalWin }) }}
          <i
            v-if="infoIcon && tournamentUi.tournamentId"
            @click="
              events.$emit('tournament-info-modal', {
                tId: tournamentUi.tournamentId,
                trackId: 'tournament-info',
              })
            "
            class="icf-info info-icon"
            data-qe-id="win-distribution-trigger"
          />
        </div>
      </div>
      <div v-if="addedPrize && tournamentUi.isMasters" class="flex">
        <div class="name">
          {{ $t('tournamentInfo.label.addedPrize') }}
        </div>
        <div class="value">
          {{
            $t('tournamentInfo.value.addedPrize', {
              numberOfSeats: tournamentUi.metaData.numberOfTickets,
              realPrize,
            })
          }}
        </div>
      </div>
      <div v-if="entryFee && !rejoin" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.entryFee') }}</div>
        <div class="value" data-qe-id="entry-fee">
          {{ $t('tournamentInfo.value.tax_fee', { fee: tournamentUi.metaData.entryFee }) }}
        </div>
      </div>
      <div v-else-if="entryFee && rejoin" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.restartFee') }}</div>
        <div class="value" data-qe-id="restart-fee">
          {{ $t('tournamentInfo.value.tax_fee', { fee: tournamentUi.metaData.restartFee }) }}
        </div>
      </div>
      <div v-if="rejoin && rejoinsRestricted" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.rejoinsRemaining') }}</div>
        <div class="value" data-qe-id="rejoins-remaining">
          {{ rejoinsRemaining }}
        </div>
      </div>
      <div v-if="competitionDate" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.competitionDate') }}</div>
        <div class="value" data-qe-id="competition-date">
          {{
            $t('tournamentInfo.value.competitionDate', {
              competitionDateStart,
              competitionDateEnd,
            })
          }}
        </div>
      </div>
      <div v-if="competitionDateLobbyTime" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.competitionDate') }}</div>
        <div class="value" data-qe-id="competition-date-lobby-time">
          {{
            $t('tournamentInfo.value.competitionDateLobbyTime', {
              competitionDateStart,
              competitionDateEnd,
              lobbyTimeStart,
              lobbyTimeEnd,
            })
          }}
        </div>
      </div>
      <div v-if="registrationDate && upcomming" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.registrationDate_upcomming') }}</div>
        <div class="value" data-qe-id="registration-date">
          {{ $t('tournamentInfo.value.registrationDate_upcomming', { registrationDateStart, registrationDateEnd }) }}
        </div>
      </div>
      <div v-else-if="registrationDate && !upcomming" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.registrationDate') }}</div>
        <div class="value" data-qe-id="registration-date">
          {{ $t('tournamentInfo.value.registrationDate', { registrationDateStart, registrationDateEnd }) }}
        </div>
      </div>
      <div v-if="lobbyTime" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.lobbyTime') }}</div>
        <div class="value" data-qe-id="lobby-time">
          {{ $t('tournamentInfo.value.lobbyTime', { lobbyTimeStart, lobbyTimeEnd }) }}
        </div>
      </div>
      <div v-if="tournamentMode" class="flex">
        <div class="name">{{ $t('tournamentInfo.label.competitionMode') }}</div>
        <div class="value" data-qe-id="competition-mode">
          {{ competitionMode }}
        </div>
      </div>
    </div>
    <p v-if="tournamentUi.gameVariant === 'speed'" class="texts-disclaimer">
      {{ $t('tournamentInfo.texts.disclaimer_speedSkat') }}
    </p>
  </div>
</template>

<script setup lang="ts">
// Info section on top of the zone selection page
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateFormat } from '@gd/i18n'
import events from '@gd/event-bus'
import { TournamentUi } from '@/model/Tournament'
import { gdcConverter } from '@gd/currency-converter'
import { TournamentStatus } from '../model/TournamentStatus'

const props = withDefaults(
  defineProps<{
    tournamentUi: TournamentUi
    headline?: string
    rejoin?: boolean
    infoIcon?: boolean
    gameName?: boolean
    totalWin?: boolean
    addedPrize?: boolean
    entryFee?: boolean
    restartFee?: boolean
    competitionDate?: boolean
    competitionDateLobbyTime?: boolean
    lobbyTime?: boolean
    registrationDate?: boolean
    tournamentMode?: boolean
  }>(),
  {
    headline: null,
    rejoin: false,
    infoIcon: true,
    gameName: false,
    totalWin: false,
    addedPrize: false,
    entryFee: false,
    restartFee: false,
    competitionDate: false,
    competitionDateLobbyTime: false,
    lobbyTime: false,
    registrationDate: false,
    tournamentMode: false,
  }
)
const { t, d } = useI18n()

/**
 * duplicated from CZoneTournamentItem
 */
const competitionDateStart = computed(() => {
  if (props.tournamentUi.dates) {
    const start = new Date(props.tournamentUi.dates.competitionStart)
    return d(start, DateFormat.DDMMYYY)
  }
  return ''
})

const playersPerGame = computed(() => {
  return props.tournamentUi.metaData.playersPerGame
})

const numberOfGames = computed(() => {
  return props.tournamentUi.metaData.numberOfGames
})

const roundsPerGame = computed(() => {
  return props.tournamentUi.metaData.roundsPerGame
})

const upcomming = computed(() => {
  // TODO the upcomming means only that the competition did not start
  // so this is actually not correct but acceptable
  return props.tournamentUi.tournamentStatus === TournamentStatus.UPCOMING
})

const competitionDateEnd = computed(() => {
  if (props.tournamentUi.dates) {
    const end = new Date(props.tournamentUi.dates.competitionEnd)
    return d(end, DateFormat.DDMMYYY)
  }
  return ''
})

const registrationDateEnd = computed(() => {
  if (props.tournamentUi.dates) {
    const endTime = new Date(props.tournamentUi.dates.registrationEnd)
    return `${d(endTime, DateFormat.DDMMYYY)}`
  }
  return ''
})

const registrationDateStart = computed(() => {
  if (props.tournamentUi.dates) {
    const startTime = new Date(props.tournamentUi.dates.registrationStart)
    return `${d(startTime, DateFormat.DDMMYYY)}`
  }
  return ''
})

const realPrize = computed(() => {
  // Format value only with EUR !!!
  return gdcConverter('125000', { gdc: 'EUR' })
})

const lobbyTimeStart = computed(() => {
  if (props.tournamentUi.dates) {
    const start = new Date(props.tournamentUi.dates.lobbyStart)
    return d(start, DateFormat.MMHH)
  }
  return ''
})
const lobbyTimeEnd = computed(() => {
  if (props.tournamentUi.dates) {
    const end = new Date(props.tournamentUi.dates.lobbyEnd)
    return d(end, DateFormat.MMHH)
  }
  return ''
})
const rejoinsRestricted = computed((): boolean => {
  return !props.tournamentUi.metaData.rejoinsUnlimited
})
const rejoinsRemaining = computed((): number => {
  return props.tournamentUi.metaData.rejoinsRemaining
})
const competitionMode = computed(() => {
  const games = t('tournamentInfo.value.competitionMode_games', numberOfGames.value)
  const rounds = t('tournamentInfo.value.competitionMode_rounds', roundsPerGame.value)
  return t('tournamentInfo.value.competitionMode', { playersPerGame: playersPerGame.value, games, rounds })
})
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import '~@npm/@gd/common-styles/dist/color-vars';
@import '../styles/vars';
.tournament-info {
  .layout {
    padding: 1em 0;
    /*background-color: white;*/
  }
  .info-icon {
    cursor: pointer;
    margin: 0 0.3em;
    color: @lobbyStatus--open;
    top: -0.1em;
    position: relative;
    &:hover {
      color: lighten(@lobbyStatus--open, 10%);
    }
  }
  .flex {
    display: flex;
  }
  .headline {
    margin-top: 0;
  }
  .name {
    padding-right: 0.5em;
    flex: 40 0;
    font-weight: bold;
    &:after {
      content: ':';
    }
  }
  .value {
    text-align: left;
    flex: 50 0;
  }
  .icon-podium {
    font-size: 0.85em;
    margin-top: -1em;
    top: 0.3em;
    position: relative;
  }
}
</style>
